import type { Section, Task } from "../content/lesson";

// ユニットの途中経過を端末に保存する。戻る・タブを閉じる・通信切れでも
// 答えた分を失わず、次に開いた時に続きから再開できるようにする。
// 完了の送信に失敗した場合も消さずに残し、次回開いた時に再送する。

export type UnitAnswer = { task_id: string; selected: string };

export type UnitProgress = {
  taskIds: string[];
  answers: UnitAnswer[];
  hearts: number;
  streak: number;
  correctCount: number;
  explanationDone: boolean;
  savedAt: number;
};

const PREFIX = "nihongo-unit-progress";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function storageKey(userId: number, sectionId: string, unitIndex: number): string {
  return `${PREFIX}:${userId}:${sectionId}:${unitIndex}`;
}

export function loadUnitProgress(userId: number, sectionId: string, unitIndex: number): UnitProgress | null {
  try {
    const raw = localStorage.getItem(storageKey(userId, sectionId, unitIndex));
    if (!raw) return null;
    const p = JSON.parse(raw) as UnitProgress;
    if (!isValidProgress(p) || Date.now() - p.savedAt > MAX_AGE_MS) {
      clearUnitProgress(userId, sectionId, unitIndex);
      return null;
    }
    return p;
  } catch {
    return null;
  }
}

/** 形が崩れた記録（古い版・手で書き換えた等）は使わない。答えは問題の並び順どおりであること */
function isValidProgress(p: UnitProgress | null): p is UnitProgress {
  if (!p || typeof p !== "object") return false;
  if (!Array.isArray(p.taskIds) || p.taskIds.length === 0 || !p.taskIds.every((id) => typeof id === "string")) return false;
  if (!Array.isArray(p.answers) || p.answers.length > p.taskIds.length) return false;
  if (!p.answers.every((a, i) => a && a.task_id === p.taskIds[i] && typeof a.selected === "string")) return false;
  return [p.hearts, p.streak, p.correctCount, p.savedAt].every((v) => typeof v === "number" && Number.isFinite(v));
}

export function saveUnitProgress(userId: number, sectionId: string, unitIndex: number, p: UnitProgress): void {
  try {
    localStorage.setItem(storageKey(userId, sectionId, unitIndex), JSON.stringify(p));
  } catch {
    // 容量超過・プライベートモード等。保存できなくても学習は続けられる
  }
}

export function clearUnitProgress(userId: number, sectionId: string, unitIndex: number): void {
  try {
    localStorage.removeItem(storageKey(userId, sectionId, unitIndex));
  } catch {
    // 無視
  }
}

/** 保存した問題IDからユニットの問題を復元する。教材が変わり1問でも見つからなければ null */
export function restoreTasks(section: Section, taskIds: string[]): Task[] | null {
  const all = new Map<string, Task>();
  for (const list of Object.values(section.pool)) {
    for (const t of list) all.set(t.id, t);
  }
  const out: Task[] = [];
  for (const id of taskIds) {
    const t = all.get(id);
    if (!t) return null;
    out.push(t);
  }
  return out.length > 0 ? out : null;
}

// ── 送れなかった完了の送り直し ───────────────────────────────
// 完了の送信が通信切れ等で失敗した答えを溜めておき、次に地図やユニットを開いた時に送る。
// ※サーバー側に重複防止が無いため、送信は成功したが応答だけ失われた場合は二重に記録されうる

const OUTBOX_PREFIX = "nihongo-unit-outbox";

type PendingUnit = { unitId: string; answers: UnitAnswer[]; savedAt: number };

export function savePendingUnit(userId: number, unitId: string, answers: UnitAnswer[]): void {
  try {
    const p: PendingUnit = { unitId, answers, savedAt: Date.now() };
    // 同じユニットを続けて送れなかった場合も上書きしないよう、時刻を鍵に含める
    localStorage.setItem(`${OUTBOX_PREFIX}:${userId}:${unitId}:${p.savedAt}`, JSON.stringify(p));
  } catch {
    // 無視
  }
}

/** 後で送り直せば通りうる失敗か（通信切れ・サーバー障害・ログイン切れ）。4xx の入力エラーは送り直さない */
export function isRetryableError(e: unknown): boolean {
  const status = (e as { status?: number } | null)?.status;
  if (typeof status !== "number") return true;
  return status >= 500 || status === 401 || status === 408 || status === 429;
}

let flushing = false;

/**
 * 溜まっている完了を送る。送れたものだけ消し、送れなければ次の機会に回す。
 * isActive が false になったら（ログアウト・別ユーザーでログイン）途中でやめる。
 * 同じ端末で複数タブを開いていても二重に送らないよう、ブラウザのロックで1つずつ流す
 */
export async function flushPendingUnits(
  userId: number,
  send: (unitId: string, answers: UnitAnswer[]) => Promise<unknown>,
  isActive: () => boolean,
): Promise<number> {
  if (flushing) return 0;
  flushing = true;
  try {
    const run = () => flushLocked(userId, send, isActive);
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    return locks ? await locks.request(OUTBOX_PREFIX, run) : await run();
  } finally {
    flushing = false;
  }
}

async function flushLocked(
  userId: number,
  send: (unitId: string, answers: UnitAnswer[]) => Promise<unknown>,
  isActive: () => boolean,
): Promise<number> {
  let sent = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(`${OUTBOX_PREFIX}:${userId}:`)) keys.push(k);
    }
    for (const k of keys) {
      if (!isActive()) break;
      const raw = localStorage.getItem(k); // 別タブが先に送って消していれば null
      if (!raw) continue;
      let p: PendingUnit;
      try {
        p = JSON.parse(raw) as PendingUnit;
        if (typeof p.unitId !== "string" || !Array.isArray(p.answers) || p.answers.length === 0) throw new Error("bad");
      } catch {
        localStorage.removeItem(k); // 壊れた記録で後ろが止まらないよう捨てる
        continue;
      }
      try {
        await send(p.unitId, p.answers);
        localStorage.removeItem(k);
        sent++;
      } catch (e) {
        if (!isRetryableError(e)) {
          localStorage.removeItem(k); // 送り直しても通らないもの
          continue;
        }
        break; // まだつながらない。次の機会に
      }
    }
  } catch {
    // localStorage が使えない環境
  }
  return sent;
}
